import * as THREE from 'three';
import { LODLevel } from '../optimization/LODSystem';

/**
 * Enum defining different projectile types
 */
export enum ProjectileType {
  BASIC,
  ROCKET,
  SCATTER,
  LASER,
  AOE,
  RAPID_FIRE
}

/**
 * Lightweight base class for all projectiles.
 * Designed to be more efficient than the full GameObject system 
 * for handling large numbers of simple, short-lived objects.
 */
export abstract class AbstractProjectile {
  protected position: THREE.Vector3;
  protected velocity: THREE.Vector3;
  protected mesh: THREE.Mesh | null = null;
  protected active: boolean = false;
  protected lifetime: number = 5.0; // seconds
  protected timeLived: number = 0;
  protected damage: number = 50;
  protected targetPosition: THREE.Vector3 | null = null;
  protected scene: THREE.Scene;
  
  // LOD properties
  protected lodLevel: LODLevel = LODLevel.HIGH;
  protected lodMeshes: Map<LODLevel, THREE.Mesh> = new Map();
  
  // Reusable vectors to avoid garbage collection
  private static readonly _tempVec3 = new THREE.Vector3();
  
  /**
   * Creates a new projectile
   * @param scene The scene to add the projectile mesh to
   */
  constructor(scene: THREE.Scene) {
    this.position = new THREE.Vector3();
    this.velocity = new THREE.Vector3();
    this.scene = scene;
  }
  
  /**
   * Abstract method to create the mesh for this projectile
   */
  public abstract createMesh(): void;
  
  /**
   * Update the projectile state
   * @param deltaTime Time in seconds since the last update
   * @returns Whether the projectile is still active
   */
  public update(deltaTime: number): boolean {
    if (!this.active) return false;
    
    // Update lifetime
    this.timeLived += deltaTime;
    if (this.timeLived >= this.lifetime) {
      this.deactivate();
      return false;
    }
    
    // Update position based on velocity
    this.position.x += this.velocity.x * deltaTime;
    this.position.y += this.velocity.y * deltaTime;
    this.position.z += this.velocity.z * deltaTime;
    
    // Update mesh position
    if (this.mesh) {
      this.mesh.position.copy(this.position);
    }
    
    return true;
  }
  
  /**
   * Called when the projectile hits something
   * @param target The object that was hit
   */
  public abstract onImpact(target: any): void;
  
  /**
   * Check if the projectile is active
   */
  public isActive(): boolean {
    return this.active;
  }
  
  /**
   * Activate the projectile
   * @param position Starting position
   * @param velocity Initial velocity
   */
  public activate(position: THREE.Vector3, velocity: THREE.Vector3, damage: number = 1): void {
    this.position.copy(position);
    this.velocity.copy(velocity);
    this.active = true;
    this.timeLived = 0;
    this.damage = damage;
    
    // Make sure we have a mesh
    if (!this.mesh) {
      this.createMesh();
    }
    
    // Add mesh to scene and make visible
    if (this.mesh) {
      this.mesh.visible = true;
      this.mesh.position.copy(position);
      
      // Add to scene if not already added
      if (!this.mesh.parent) {
        this.scene.add(this.mesh);
      }
    }
  }
  
  /**
   * Deactivate the projectile
   */
  public deactivate(): void {
    this.active = false;
    this.targetPosition = null;
    
    if (this.mesh) {
      this.mesh.visible = false;
      
      // Remove from scene to reduce draw calls
      if (this.mesh.parent) {
        this.mesh.parent.remove(this.mesh);
      }
    }
  }
  
  /**
   * Set the LOD level for this projectile
   * @param level The LOD level to set
   */
  public setLODLevel(level: LODLevel): void {
    if (this.lodLevel === level) return;
    
    this.lodLevel = level;
    
    // Switch mesh if we have one for this level
    const newMesh = this.lodMeshes.get(level);
    if (newMesh) {
      // Remember position/visibility of current mesh
      const position = this.position.clone();
      const wasVisible = this.mesh ? this.mesh.visible : false;
      
      // Remove current mesh from scene
      if (this.mesh && this.mesh.parent) {
        this.mesh.parent.remove(this.mesh);
      }
      
      // Set new mesh
      this.mesh = newMesh;
      
      // Update new mesh properties
      this.mesh.position.copy(position);
      this.mesh.visible = wasVisible;
      
      // Add to scene if it should be visible
      if (wasVisible && !this.mesh.parent) {
        this.scene.add(this.mesh);
      }
    }
  }
  
  /**
   * Get the current LOD level
   */
  public getLODLevel(): LODLevel {
    return this.lodLevel;
  }
  
  /**
   * Get the current damage value
   */
  public getDamage(): number {
    return this.damage;
  }
  
  /**
   * Get the current position
   */
  public getPosition(): THREE.Vector3 {
    return this.position;
  }
  
  /**
   * Get the velocity vector
   */
  public getVelocity(): THREE.Vector3 {
    return this.velocity;
  }
  
  /**
   * Set a target position for guided projectiles
   */
  public setTargetPosition(target: THREE.Vector3): void {
    this.targetPosition = target.clone();
  }
  
  /**
   * Check if the projectile has a target position
   */
  public hasTarget(): boolean {
    return this.targetPosition !== null;
  }
  
  /**
   * Calculate distance to a point or object
   * @param point Position to check distance to
   */
  public distanceTo(point: THREE.Vector3): number {
    return this.position.distanceTo(point);
  }
  
  /**
   * Calculate squared distance to a point or object
   * More efficient than distanceTo as it avoids square root
   * @param point Position to check distance to
   */
  public distanceToSquared(point: THREE.Vector3): number {
    return this.position.distanceToSquared(point);
  }
} 